import { useState, useEffect, useRef, useCallback } from "react";
import { X } from "lucide-react";

export function ImageLightbox({ src, alt, onClose, "data-testid": testId = "image-lightbox" }) {
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef({ startX: 0, startY: 0, posX: 0, posY: 0 });
  const didDrag = useRef(false);
  const lastTouchDist = useRef(null);
  const [displaySrc, setDisplaySrc] = useState(null);

  // If src is a blob URL, use directly. Otherwise fetch with auth.
  useEffect(() => {
    if (!src) return;
    if (src.startsWith("blob:") || src.startsWith("data:")) {
      setDisplaySrc(src);
      return;
    }
    let cancelled = false;
    const token = localStorage.getItem("session_token");
    fetch(src, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then(res => res.ok ? res.blob() : null)
      .then(blob => {
        if (blob && !cancelled) setDisplaySrc(URL.createObjectURL(blob));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [src]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "+" || e.key === "=") setScale(s => Math.min(s + 0.5, 5));
      if (e.key === "-") setScale(s => Math.max(s - 0.5, 0.5));
      if (e.key === "0") { setScale(1); setPosition({ x: 0, y: 0 }); }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [onClose]);

  // Mouse wheel zoom
  const handleWheel = useCallback((e) => {
    e.preventDefault();
    setScale(s => Math.max(0.5, Math.min(5, s + (e.deltaY > 0 ? -0.2 : 0.2))));
  }, []);

  // --- Mouse drag ---
  const handleMouseDown = useCallback((e) => {
    if (scale <= 1) return;
    e.preventDefault();
    setDragging(true);
    didDrag.current = false;
    dragRef.current = { startX: e.clientX, startY: e.clientY, posX: position.x, posY: position.y };
  }, [scale, position]);

  const handleMouseMove = useCallback((e) => {
    if (!dragging) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    if (Math.abs(dx) > 3 || Math.abs(dy) > 3) didDrag.current = true;
    setPosition({ x: dragRef.current.posX + dx, y: dragRef.current.posY + dy });
  }, [dragging]);

  const handleMouseUp = useCallback(() => setDragging(false), []);

  // --- Touch drag + pinch-to-zoom ---
  const handleTouchStart = useCallback((e) => {
    if (e.touches.length === 1 && scale > 1) {
      setDragging(true);
      didDrag.current = false;
      const t = e.touches[0];
      dragRef.current = { startX: t.clientX, startY: t.clientY, posX: position.x, posY: position.y };
    }
    if (e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      lastTouchDist.current = Math.hypot(dx, dy);
    }
  }, [scale, position]);

  const handleTouchMove = useCallback((e) => {
    e.preventDefault();
    if (e.touches.length === 1 && dragging) {
      const t = e.touches[0];
      const dx = t.clientX - dragRef.current.startX;
      const dy = t.clientY - dragRef.current.startY;
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) didDrag.current = true;
      setPosition({ x: dragRef.current.posX + dx, y: dragRef.current.posY + dy });
    }
    if (e.touches.length === 2 && lastTouchDist.current) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const dist = Math.hypot(dx, dy);
      const delta = (dist - lastTouchDist.current) * 0.01;
      lastTouchDist.current = dist;
      setScale(s => Math.max(0.5, Math.min(5, s + delta)));
    }
  }, [dragging]);

  const handleTouchEnd = useCallback(() => {
    setDragging(false);
    lastTouchDist.current = null;
  }, []);

  const handleImageClick = useCallback(() => {
    if (!didDrag.current) {
      if (scale === 1) {
        setScale(2);
      } else {
        setScale(1);
        setPosition({ x: 0, y: 0 });
      }
    }
  }, [scale]);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
      onClick={(e) => { if (e.target === e.currentTarget && !didDrag.current) onClose(); }}
      onWheel={handleWheel}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      data-testid={testId}
    >
      <div className="absolute top-4 right-4 flex items-center gap-2 z-10">
        <button onClick={() => setScale(s => Math.min(s + 0.5, 5))} className="w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 text-white flex items-center justify-center text-lg font-bold" data-testid="lightbox-zoom-in">+</button>
        <span className="text-white text-sm font-medium min-w-[3rem] text-center">{Math.round(scale * 100)}%</span>
        <button onClick={() => setScale(s => Math.max(s - 0.5, 0.5))} className="w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 text-white flex items-center justify-center text-lg font-bold" data-testid="lightbox-zoom-out">−</button>
        <button onClick={() => { setScale(1); setPosition({ x: 0, y: 0 }); }} className="px-3 h-10 rounded-full bg-white/20 hover:bg-white/30 text-white text-sm">Reset</button>
        <button onClick={onClose} className="w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 text-white flex items-center justify-center" data-testid="lightbox-close">
          <X className="w-5 h-5" />
        </button>
      </div>
      {alt && <p className="absolute top-4 left-4 text-white/80 text-sm z-10">{alt}</p>}
      <div
        className="overflow-hidden w-full h-full flex items-center justify-center"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{ touchAction: "none" }}
      >
        {displaySrc ? (
          <img
            src={displaySrc}
            alt={alt || ""}
            className="max-w-none select-none"
            style={{
              transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
              cursor: scale > 1 ? (dragging ? "grabbing" : "grab") : "zoom-in",
              transition: dragging ? "none" : "transform 0.2s ease",
            }}
            onMouseDown={handleMouseDown}
            onClick={handleImageClick}
            draggable={false}
          />
        ) : (
          <div className="text-white/50">Laadin...</div>
        )}
      </div>
    </div>
  );
}
